import { createSignal, Show } from 'solid-js';
import { Button, Switch, Textarea } from '@components';
import { useSettings, useLicense } from '@util/context';
import API from '@util/api';

export default function CloudflareStatus(props) {
	const { settings, saveSettings } = useSettings();
	const { isLocked, shakePromo } = useLicense();

	const [purgeInput, setPurgeInput] = createSignal('');
	const [purging, setPurging] = createSignal(false);
	const [purgingAll, setPurgingAll] = createSignal(false);
	const [message, setMessage] = createSignal(null);
	const [disconnecting, setDisconnecting] = createSignal(false);
	const [showDisconnect, setShowDisconnect] = createSignal(false);

	// Get domain from route_pattern (e.g., "example.com/*" -> "example.com")
	const getDomain = () => {
		const pattern = props.status?.route_pattern || props.status?.zone_name || '';
		return pattern.replace(/\/\*$/, '');
	};

	const purge = async () => {
		const input = purgeInput().trim();
		if (!input) return;

		setPurging(true);
		setMessage(null);

		// Split by newlines for multiple URLs/patterns
		const urls = input.split('\n').map(u => u.trim()).filter(Boolean);

		try {
			const res = await API.post('cf/purge', { urls });
			if (res.data?.success) {
				setMessage({ type: 'success', text: `Purged ${urls.length} URL${urls.length > 1 ? 's' : ''}` });
				setPurgeInput('');
			} else {
				setMessage({ type: 'error', text: res.data?.message || 'Failed to purge' });
			}
		} catch (err) {
			setMessage({ type: 'error', text: 'Connection error' });
		} finally {
			setPurging(false);
			setTimeout(() => setMessage(null), 4000);
		}
	};

	const purgeAll = async () => {
		setPurgingAll(true);
		setMessage(null);

		try {
			const res = await API.post('cf/purge-all');
			if (res.data?.success) {
				setMessage({ type: 'success', text: 'Entire cache purged' });
			} else {
				setMessage({ type: 'error', text: res.data?.message || 'Failed to purge' });
			}
		} catch (err) {
			setMessage({ type: 'error', text: 'Connection error' });
		} finally {
			setPurgingAll(false);
			setTimeout(() => setMessage(null), 4000);
		}
	};

	const disconnect = async () => {
		setDisconnecting(true);
		try {
			const res = await API.post('cf/teardown');
			if (res.data?.success) {
				props.onDisconnected?.();
			}
		} catch (err) {
			console.error('Teardown error:', err);
		} finally {
			setDisconnecting(false);
			setShowDisconnect(false);
		}
	};

	return (
		<div class="ap-space-y-4">
			{/* Connection + Purge Card */}
			<div class="ap-p-5 ap-bg-white ap-rounded-lg ap-border ap-border-slate-200 ap-space-y-4">
				{/* Connected Status */}
				<div class="ap-flex ap-items-center ap-gap-2 ap-text-sm">
					<Show when={props.checking} fallback={
						<div class="ap-w-2 ap-h-2 ap-bg-green-500 ap-rounded-full" />
					}>
						<div class="ap-w-3 ap-h-3 ap-border-2 ap-border-green-500 ap-border-t-transparent ap-rounded-full ap-animate-spin" />
					</Show>
					<span class="ap-text-green-700 ap-font-medium">{getDomain()}</span>
					<Show when={props.checking}>
						<span class="ap-text-slate-400 ap-text-xs">verifying...</span>
					</Show>
					<span class="ap-text-slate-300">|</span>
					<button
						class="ap-text-slate-500 hover:ap-text-red-600 ap-transition-colors"
						onClick={() => setShowDisconnect(true)}
					>
						Disconnect
					</button>
				</div>

				{/* Disconnect Confirmation */}
				<Show when={showDisconnect()}>
					<div class="ap-p-3 ap-bg-red-50 ap-border ap-border-red-200 ap-rounded-lg ap-flex ap-items-center ap-justify-between">
						<span class="ap-text-sm ap-text-red-700">Remove worker and route?</span>
						<div class="ap-flex ap-gap-2">
							<Button variant="danger" size="sm" onClick={disconnect} loading={disconnecting() ? 'Removing...' : ''}>
								Yes
							</Button>
							<Button variant="secondary" size="sm" onClick={() => setShowDisconnect(false)}>
								Cancel
							</Button>
						</div>
					</div>
				</Show>

				{/* Purge Cache */}
				<div class="ap-flex ap-items-center ap-gap-2">
					<input
						type="text"
						class="ap-flex-1 ap-px-3 ap-py-2 ap-text-sm ap-border ap-border-slate-300 ap-rounded-lg focus:ap-outline-none focus:ap-ring-2 focus:ap-ring-indigo-500 focus:ap-border-transparent"
						placeholder="URL or pattern (e.g., /blog/*)"
						value={purgeInput()}
						onInput={(e) => setPurgeInput(e.target.value)}
						onKeyDown={(e) => e.key === 'Enter' && purge()}
					/>
					<Button
						variant="secondary"
						size="sm"
						onClick={purge}
						loading={purging() ? 'Purging...' : ''}
						disabled={!purgeInput().trim()}
					>
						Purge
					</Button>
					<Button
						variant="danger"
						size="sm"
						onClick={purgeAll}
						loading={purgingAll() ? 'Purging...' : ''}
					>
						Purge All
					</Button>
					<Show when={message()}>
						<span
							class="ap-text-xs ap-px-2 ap-py-1 ap-rounded ap-whitespace-nowrap"
							classList={{
								'ap-bg-green-100 ap-text-green-700': message().type === 'success',
								'ap-bg-red-100 ap-text-red-700': message().type === 'error',
							}}
						>
							{message().text}
						</span>
					</Show>
				</div>
			</div>

			{/* Settings Card */}
			<div class="ap-p-5 ap-bg-white ap-rounded-lg ap-border ap-border-slate-200 ap-space-y-4">
				<h4 class="ap-text-sm ap-font-semibold ap-text-slate-700">Settings</h4>

				<Switch
					value={settings.cf_auto_purge}
					onChange={(v) => isLocked() ? shakePromo() : settings.cf_auto_purge = v}
					locked={isLocked()}
					subtitle="Purge related URLs when posts, terms, or comments change."
					instantSave={true}
				>
					Auto-purge on content changes
				</Switch>

				<div class="ap-space-y-1">
					<label class="ap-block ap-text-sm ap-font-medium ap-text-slate-700">
						Bypass Patterns
					</label>
					<Textarea
						value={settings.cf_bypass_patterns}
						onInput={(e) => {
							if (isLocked()) {
								shakePromo();
								return;
							}
							settings.cf_bypass_patterns = e.target.value;
						}}
						placeholder="/wp-admin&#10;/checkout&#10;/cart"
						rows={2}
						disabled={isLocked()}
					/>
					<p class="ap-text-xs ap-text-slate-500">
						One pattern per line. Matching URLs skip cache.
					</p>
				</div>
			</div>
		</div>
	);
}
